import _ from 'lodash';
import { curveMonotoneX, line, select, svg } from 'd3';
import { layout } from 'ciena-dagre';
import graphLib from 'ciena-graphlib';

const LABEL_HEIGHT = 15;
const MAX_LABEL_CHARS = 35;
const DEFAULT_NODE_TYPE = {
    name: 'Node',
    iconClass: 'material-icons node-icon',
    icon: 'fiber_manual_record',
    iconHeight: 24,
    textClass: 'node-labels',
    textAnchor: 'middle',
};
const DEFAULT_EDGE_TYPE = {
    name: 'Edge',
    className: 'visible-edge',
};
const GRAPH_CONFIG = {
    rankdir: 'LR', // Paths flow from left-to-right
    ranker: 'network-simplex', // Ranking strategy for node placement. longest-path seems to align nPoints and Servers, which we want
    ranksep: 200, // Horizontal margin between nodes. Needs to be large enough to accommodate the length of the labels
    nodesep: 75, // Vertical margin between nodes
    marginx: 15, // Amount of room on the sides of the graph. Needs to be large enough to accommodate the length of the labels
    marginy: 15, // Amount of room on the top and bottom of the graph. Needs to be large enough to accommodate the height of the labels
};
const NODE_DEFAULTS = {
    width: 1,
    height: 1,
};

/**
 * Processes node labels
 * @param {Array} labels Node labels
 * @returns {Array} Processed node labels
 */
function processLabels(node, labels) {
    return _.chain(labels)
        .map((label) => {
            return _.isFunction(label) ? label(node) : label;
        })
        .compact()
        .flatMap((labelText) => {
            return wrapOrTruncateLabel(labelText);
        })
        .value();
}

/**
 * Attempts to wrap a label that is too long.  If the label can't be wrapped, it will truncate the label and add
 * ellipsis.
 *
 * @param {String} label The label to wrap or truncate.
 *
 * @returns {Array | String} If the label could be wrapped, an array with the wrapped chunks will be returned.  If
 * the label could not be wrapped, a string will be returned.
 */
function wrapOrTruncateLabel(label) {
    if (label.length > MAX_LABEL_CHARS) {
        // See if we can split the string on whitespace.
        const labelWords = label.split(/\s/);
        if (labelWords.length > 1) {
            const fragments = _.chunk(labelWords, Math.ceil(labelWords.length / 2));
            const val = _.map(fragments, (fragmentWords) => wrapOrTruncateLabel(fragmentWords.join(' ')));
            return _.flatten(val);
        }

        // Couldn't split the string, so truncate and add ellipsis instead.
        return `${label.substring(0, MAX_LABEL_CHARS - 3)}...`;
    }

    return label;
}

/**
 * Draws edges between nodes.
 * @param {Object} graphGroup d3 selection of specified svg element
 * @param {Object} graph Graph object representing trace graph
 * @param {Object} options The options to use when drawing the edges.
 */
function drawEdges(graphGroup, graph, options) {
    const edgeTypes = options?.edgeTypes;
    const lineFunction = line()
        .x((d) => d.x)
        .y((d) => d.y)
        .curve(curveMonotoneX);

    // Create the edges.
    const graphEdges = graphGroup
        .selectAll('path')
        .data(graph.edges())
        .enter()
        .append('path')
        .classed('graph-edge', true)
        .attr('d', (edge) => {
            return lineFunction(graph.edge(edge).points);
        });

    Object.keys(edgeTypes).forEach((edgeType) => {
        const typeDetails = edgeTypes?.[edgeType] || DEFAULT_EDGE_TYPE;
        const getProperty = (property, d) => {
            const typeProperty = typeDetails?.[property];

            if (_.isFunction(typeProperty)) {
                return typeProperty(graph.node(d));
            }

            return typeProperty;
        };

        // Add the class for each node type.
        graphEdges
            .filter((edge) => graph.edge(edge).type === typeDetails.name)
            .each(function renderEdge(edge) {
                const className = getProperty('className', edge);
                select(this).classed(className, true);
            });
    });

    return graphEdges;
}

/**
 * Draws nodes and all associated information onto the specified svg element
 * @param {Object} graphGroup d3 selection of specified svg element
 * @param {Object} graph Graph object representing trace graph
 * @param {Object} options The graph options to use when drawing the nodes.
 */
function drawNodes(graphGroup, graph, options) {
    const nodeTypes = options?.nodeTypes;

    // Create the nodes
    const graphNodes = graphGroup
        .selectAll('g.graph-node')
        .data(graph.nodes())
        .enter()
        .append('g')
        .classed('graph-node', true)
        .attr('x', (d) => graph.node(d).x)
        .attr('y', (d) => graph.node(d).y)
        .attr('width', (d) => graph.node(d).width)
        .attr('height', (d) => graph.node(d).height);

    Object.keys(nodeTypes).forEach((nodeType) => {
        const typeDetails = nodeTypes?.[nodeType] || DEFAULT_NODE_TYPE;
        const getProperty = (property, d) => {
            const typeProperty = typeDetails?.[property];

            if (_.isFunction(typeProperty)) {
                return typeProperty(graph.node(d));
            }

            return typeProperty;
        };

        // Add the icons and labels for each node type.
        graphNodes
            .filter((node) => graph.node(node).type === typeDetails.name)
            .each(function renderNode(node) {
                const nodeGroup = select(this);

                // Apply node offsets if they exist.
                const nodeOffset = getProperty('offset', node);
                if (_.isObject(nodeOffset)) {
                    nodeGroup.attr('transform', `translate(${nodeOffset?.x || 0},${nodeOffset?.y || 0})`);
                }

                // Apply the tooltip if it exists.
                const tooltip = getProperty('tooltip', node);
                if (_.isString(tooltip)) {
                    nodeGroup.append('title').text(tooltip);
                }

                // Add node icons.
                const iconType = getProperty('iconType', node);
                const iconClass = getProperty('iconClass', node);
                const iconWidth = getProperty('iconWidth', node);
                const iconHeight = getProperty('iconHeight', node);
                const icon = getProperty('icon', node);
                switch (iconType) {
                    case 'svg':
                        svg(icon).then((svgIcon) => {
                            const outlineWidth = getProperty('outlineWidth', node);
                            const outlineHeight = getProperty('outlineHeight', node);
                            const iconGroup = nodeGroup.append('g').attr('transform', `translate(0,-${iconHeight})`);

                            iconGroup
                                .append('rect')
                                .classed('node-outline', true)
                                .classed(iconClass, true)
                                .attr('width', outlineWidth)
                                .attr('height', outlineHeight)
                                .attr('x', (_d) => graph.node(_d).x - outlineWidth / 2)
                                .attr('y', (_d) => graph.node(_d).y + outlineHeight / 2);

                            iconGroup
                                .append(() => svgIcon.documentElement)
                                .classed('node-icon', true)
                                .classed(iconClass, true)
                                .attr('width', iconWidth)
                                .attr('height', iconHeight)
                                .attr('x', (_d) => graph.node(_d).x - iconWidth / 2)
                                .attr('y', (_d) => graph.node(_d).y + iconHeight / 2);
                        });
                        break;
                    default:
                        ['node-outline', 'node-icon'].forEach((className) => {
                            nodeGroup
                                .append('text')
                                .classed(className, true)
                                .classed(iconClass, true)
                                .attr('x', (_d) => graph.node(_d).x)
                                .attr('y', (_d) => graph.node(_d).y + iconHeight / 2)
                                .attr('text-anchor', 'middle')
                                .text(icon);
                        });
                        break;
                }

                // Add node labels.
                const textClass = getProperty('textClass', node);
                const textAnchor = getProperty('textAnchor', node);
                const textOffset = getProperty('textOffset', node);
                nodeGroup
                    .append('text')
                    .classed(textClass, true)
                    .attr('text-anchor', textAnchor)
                    .attr('x', (_d) => graph.node(_d).x)
                    .attr('y', (_d) => graph.node(_d).y + textOffset)
                    .each(function renderNodeLabels(_d) {
                        const nodeDetails = graph.node(_d);
                        const labels = processLabels(nodeDetails, typeDetails.labels);
                        select(this)
                            .selectAll(textClass)
                            .data(labels)
                            .enter()
                            .append('tspan')
                            .attr('dy', LABEL_HEIGHT)
                            .attr('x', graph.node(_d).x)
                            .text(_.identity);
                    });
            });
    });

    return graphNodes;
}

function drawMetrics(svgGroup, data) {
    // Create the metrics
    const metrics = svgGroup
        .selectAll('g.graph-metric')
        .data(data.metrics)
        .enter()
        .append('g')
        .classed('graph-metric', true)
        .attr('transform', (metric) => `translate(0, ${metric.y})`);

    metrics.each(function renderMetric(metric) {
        const metricElement = select(this);

        metricElement
            .append('text')
            .classed('graph-metric-value', true)
            .attr('x', 0)
            .attr('y', 30)
            .attr('text-anchor', 'middle')
            .text(metric.value);

        metricElement
            .append('text')
            .classed('graph-metric-title', true)
            .attr('x', 0)
            .attr('y', 45)
            .attr('text-anchor', 'middle')
            .text(metric.title);
    });

    return metrics;
}

/**
 * Completes additional steps required to fine-tune positioning on nodes during layout process
 * @param {Object} graph Graph object that has not yet been run through layout algorithm
 */
function doLayout(graph) {
    // Add extra node to beginning and end of graph
    const originNode = 'ORIGIN_NODE';
    const destinationNode = 'DESTINATION_NODE';
    graph.setNode(originNode);
    graph.setNode(destinationNode);

    // Point origin node to sources
    graph.sources().forEach((source) => graph.setEdge(originNode, source, { weight: 200 }));

    // Point sinks to destination node
    // (the weight is smaller here so an nPoint that has no edges [one that is a source and a sink] will align left instead of right)
    graph.sinks().forEach((sink) => graph.setEdge(sink, destinationNode, { weight: 100 }));

    // Run layout
    layout(graph);

    // Remove extra edges
    graph.outEdges(originNode).forEach((edge) => graph.removeEdge(edge.v, edge.w));
    graph.inEdges(destinationNode).forEach((edge) => graph.removeEdge(edge.v, edge.w));

    // Remove extra nodes
    graph.removeNode(originNode);
    graph.removeNode(destinationNode);
}

/**
 * Draws graph to specified svg element and bind event listeners
 * @param {Object} svgElement d3 selection of specified svg element
 * @param {Object} data Graph data
 * @param {Object} options The graph options.
 */
export default function drawGraph(svgElement, data, options) {
    const rootSvg = select(svgElement);

    rootSvg.selectAll('*').remove();

    const graph = new graphLib.Graph().setGraph(GRAPH_CONFIG).setDefaultEdgeLabel(() => {
        return { label: '' };
    });

    _.forEach(data.nodes, (node) => {
        graph.setNode(node.id, _.defaults(node, NODE_DEFAULTS));
    });

    _.forEach(data.edges, (edge) => {
        graph.setEdge(edge.from, edge.to, edge);
    });

    doLayout(graph);

    const graphGroup = rootSvg.append('g');
    drawEdges(graphGroup, graph, options);
    const graphNodes = drawNodes(graphGroup, graph, options);

    const metricsGroup = rootSvg.append('g');
    drawMetrics(metricsGroup, data);

    // Return a callback that can be used to resize the graph.
    return (containerWidth) => {
        // TODO: Clean this up.  It's complex and has some magic numbers...
        const metricsBounds = metricsGroup.node()?.getBoundingClientRect();
        const firstNodeBounds = _.first(graphNodes.nodes())?.getBoundingClientRect();
        const spaceRequiredForMetrics = (metricsBounds?.width || 0) + GRAPH_CONFIG.nodesep + GRAPH_CONFIG.marginx * 2;
        const graphHeight = graph.graph()?.height;
        const svgHeight = Math.max(graphHeight, 145);
        const graphWidth = graph.graph()?.width;
        const graphOffset =
            GRAPH_CONFIG.marginx + GRAPH_CONFIG.ranksep + NODE_DEFAULTS.width / 2 - (firstNodeBounds?.width || 0) / 2;
        const graphLeftOffset = graphOffset - spaceRequiredForMetrics;
        const graphScale =
            graphWidth - graphLeftOffset > containerWidth
                ? 1 - ((graphWidth - graphLeftOffset) / containerWidth - 1)
                : 1;
        const scaledGraphWidth = graphWidth * graphScale;
        const scaledGraphHeight = graphHeight * graphScale;
        const graphX = containerWidth / 2 - scaledGraphWidth / 2;
        const graphY = svgHeight / 2 - scaledGraphHeight / 2;
        const metricsX = (metricsBounds?.width || 0) / 2 + GRAPH_CONFIG.marginx * 2;
        const metricsY = svgHeight / 2 - (metricsBounds?.height || 0) / 2;

        rootSvg.attr('height', svgHeight).attr('width', containerWidth);

        graphGroup.attr('transform', `translate(${graphX}, ${graphY}) scale(${graphScale})`);
        metricsGroup.attr('transform', `translate(${metricsX}, ${metricsY}) scale(${graphScale})`);
    };
}
